// Keep WordPress in charge of the modal and desktop dropdowns. The mobile
// view reads the same editor-owned links, but shows one navigation level at
// a time; it never writes to core's submenu state or aria-expanded bindings.
import { initMenuSwipeBack } from "./mobile-navigation-swipe";

const MENU_CLASS = "himml-mobile-menu";
const READY_CLASS = "himml-mobile-drilldown";
const ITEM_LINK = ":scope > .wp-block-navigation-item__content";
const SUBMENU = ":scope > .wp-block-navigation__submenu-container";
// A second tap this soon and this close to the one that opened a level is
// the rest of a double tap, not a choice in the new level.
const DOUBLE_TAP_MS = 500;
const DOUBLE_TAP_RADIUS = 40;

const labelOf = (element) => element.textContent.replace(/\s+/g, " ").trim();

const copyLinkAttributes = (source, link) => {
  [
    "href",
    "target",
    "rel",
    "hreflang",
    "download",
    "aria-current",
    "aria-label",
  ].forEach((attribute) => {
    if (source?.hasAttribute(attribute)) {
      link.setAttribute(attribute, source.getAttribute(attribute));
    } else {
      link.removeAttribute(attribute);
    }
  });
};

const createLink = (source, className) => {
  const link = document.createElement("a");
  copyLinkAttributes(source, link);
  link.className = className;
  link.textContent = labelOf(source);
  return link;
};

const createMenu = (container) => {
  const content = container.querySelector(
    ".wp-block-navigation__responsive-container-content"
  );
  const sourceList = content?.querySelector(
    ":scope > .wp-block-navigation__container"
  );
  const navigation = container.closest("nav");
  const openButton = navigation?.querySelector(
    ".wp-block-navigation__responsive-container-open"
  );
  const closeButton = container.querySelector(
    ".wp-block-navigation__responsive-container-close"
  );
  if (
    !sourceList ||
    !openButton ||
    !closeButton ||
    container.classList.contains(READY_CLASS)
  ) {
    return;
  }

  const menu = document.createElement("div");
  menu.className = MENU_CLASS;

  const brand = document.createElement("div");
  brand.className = `${MENU_CLASS}__brand`;
  const sourceLogo = (navigation.closest("header") || document).querySelector(
    ".wp-block-site-logo"
  );
  let logoLink = null;
  if (sourceLogo) {
    const logo = sourceLogo.cloneNode(true);
    logo.className = `${MENU_CLASS}__logo`;
    [logo, ...logo.querySelectorAll("*")].forEach((element) => {
      element.removeAttribute("id");
      [...element.attributes].forEach(({ name }) => {
        if (name.startsWith("data-wp-")) element.removeAttribute(name);
      });
    });
    logoLink = logo.querySelector("a[href]");
    brand.append(logo);
  }

  const syncChrome = () => {
    const buttonRect = openButton.getBoundingClientRect();
    const logoRect = sourceLogo?.querySelector("img")?.getBoundingClientRect();
    if (!buttonRect.width || !logoRect?.width) return;
    const headerRect = navigation.closest("#site-header")?.getBoundingClientRect();
    const values = {
      "logo-x": logoRect.left,
      "logo-y": logoRect.top,
      "logo-width": logoRect.width,
      "close-y": buttonRect.top,
      "close-width": buttonRect.width,
      "close-height": buttonRect.height,
      "close-right": document.documentElement.clientWidth - buttonRect.right,
      "brand-height": headerRect?.bottom || Math.max(logoRect.bottom, buttonRect.bottom) + 12,
    };
    Object.entries(values).forEach(([name, value]) => {
      container.style.setProperty(`--himml-menu-${name}`, `${value}px`);
    });
  };
  openButton.addEventListener("click", syncChrome, true);

  const path = document.createElement("div");
  path.className = `${MENU_CLASS}__path`;
  const back = document.createElement("button");
  back.type = "button";
  back.className = `${MENU_CLASS}__back`;
  back.textContent = "Zurück";
  const title = document.createElement("h2");
  title.className = `${MENU_CLASS}__title`;
  title.tabIndex = -1;
  const titleLink = document.createElement("a");
  title.append(titleLink);
  path.append(back, title);

  const viewport = document.createElement("div");
  viewport.className = `${MENU_CLASS}__viewport`;
  const panels = [];
  let trail = [];
  let levelTap = null;

  const render = ({ animate = false, focus = null } = {}) => {
    const current = trail[trail.length - 1];
    menu.dataset.animate = String(animate);
    menu.dataset.depth = String(trail.length - 1);
    back.setAttribute(
      "aria-label",
      trail.length > 2
        ? `Zurück zu ${trail[trail.length - 2].label}`
        : "Zurück zum Hauptmenü"
    );
    titleLink.textContent = current.label;

    // Make the destination focusable before hiding the previous level.
    current.element.inert = false;
    current.element.removeAttribute("aria-hidden");
    current.element.dataset.position = "current";
    focus?.focus({ preventScroll: true });
    // Keep the link node stable and hand off focus before removing its href
    // when returning to the main menu, including Escape from the title link.
    copyLinkAttributes(current.parentLink, titleLink);
    // Hide Back only after handing off focus. Hiding the focused button first
    // fires focusout with relatedTarget=null, which WordPress treats as an
    // instruction to close the entire navigation overlay.
    back.hidden = trail.length === 1;
    panels.forEach((panel) => {
      const active = panel === current;
      panel.element.dataset.position = active
        ? "current"
        : trail.includes(panel)
        ? "before"
        : "after";
      panel.element.inert = !active;
      if (!active) panel.element.setAttribute("aria-hidden", "true");
      panel.trigger?.setAttribute(
        "aria-expanded",
        String(trail.includes(panel))
      );
    });
  };

  const buildPanel = (list, label, parentLink = null, trigger = null) => {
    const element = document.createElement("div");
    element.className = `${MENU_CLASS}__panel`;
    element.id = `${container.id}-level-${panels.length}`;
    element.dataset.position = "after";
    element.inert = true;
    element.setAttribute("aria-hidden", "true");
    element.setAttribute("role", "group");
    element.setAttribute("aria-label", label);
    const panel = { element, label, trigger, parentLink };
    panels.push(panel);
    viewport.append(element);

    if (parentLink?.hasAttribute("href")) {
      const overview = createLink(parentLink, `${MENU_CLASS}__overview`);
      overview.textContent = "Zur Übersicht";
      overview.setAttribute("aria-label", `${label}: Zur Übersicht`);
      element.append(overview);
    }

    const items = document.createElement("ul");
    items.className = `${MENU_CLASS}__list`;
    element.append(items);
    [...list.children].forEach((item) => {
      const source = item.querySelector(ITEM_LINK);
      if (!source) return;
      const row = document.createElement("li");
      const submenu = item.querySelector(SUBMENU);
      if (submenu) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = `${MENU_CLASS}__item ${MENU_CLASS}__next`;
        button.textContent = labelOf(source);
        button.setAttribute(
          "aria-label",
          `${labelOf(source)}: Untermenü öffnen`
        );
        const child = buildPanel(submenu, labelOf(source), source, button);
        button.setAttribute("aria-controls", child.element.id);
        button.setAttribute("aria-expanded", "false");
        // Show the way to the current page while it is still levels away.
        if (
          source.hasAttribute("aria-current") ||
          submenu.querySelector("[aria-current]")
        ) {
          button.dataset.currentBranch = "";
        }
        button.addEventListener("click", (event) => {
          levelTap =
            event.detail > 0
              ? {
                  panel: child,
                  x: event.clientX,
                  y: event.clientY,
                  time: event.timeStamp,
                }
              : null;
          trail.push(child);
          child.element.scrollTop = 0;
          render({ animate: event.detail > 0, focus: title });
        });
        row.append(button);
      } else if (source.hasAttribute("href")) {
        row.append(createLink(source, `${MENU_CLASS}__item`));
      }
      if (row.childElementCount) items.append(row);
    });
    return panel;
  };

  const root = buildPanel(sourceList, "Menü");
  trail = [root];
  const goBack = (animate) => {
    if (trail.length < 2) return;
    const previous = trail.pop();
    render({ animate, focus: previous.trigger });
  };
  back.addEventListener("click", (event) => goBack(event.detail > 0));
  initMenuSwipeBack(viewport, menu, () => trail, goBack);

  // The second tap of a double tap lands on whatever slid in under the finger,
  // often "Zur Übersicht", and would leave the page. Keyboard activation has
  // detail 0 and is never held back.
  viewport.addEventListener(
    "click",
    (event) => {
      if (
        event.detail > 0 &&
        levelTap?.panel === trail[trail.length - 1] &&
        event.timeStamp - levelTap.time < DOUBLE_TAP_MS &&
        Math.hypot(event.clientX - levelTap.x, event.clientY - levelTap.y) <
          DOUBLE_TAP_RADIUS
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );

  const dock = document.createElement("div");
  dock.className = `${MENU_CLASS}__dock`;
  const phoneSource = content.querySelector('a[href^="tel:"]');
  if (phoneSource) {
    const availability = document.createElement("p");
    availability.textContent = "Tag und Nacht für Sie erreichbar";
    dock.append(availability, createLink(phoneSource, `${MENU_CLASS}__phone`));
  }
  const contactSource = [
    ...sourceList.querySelectorAll(":scope > li > a[href]"),
  ].find(
    (link) => new URL(link.href).pathname.replace(/\/$/, "") === "/kontakt"
  );
  if (contactSource) {
    dock.append(createLink(contactSource, `${MENU_CLASS}__contact`));
  }
  menu.append(brand, path, viewport, dock);
  content.append(menu);
  container.classList.add(READY_CLASS);
  render();

  const isOpen = () => container.classList.contains("has-modal-open");
  let wasOpen = isOpen();
  // Core focuses the first focusable element one frame after opening, which
  // is the cloned logo link. Hand that first focus on to the first menu entry
  // so keyboard and screen reader users start in the menu, not on "home".
  let redirectOpeningFocus = false;
  container.addEventListener("focusin", (event) => {
    if (!redirectOpeningFocus) return;
    redirectOpeningFocus = false;
    if (event.target !== logoLink) return;
    trail[trail.length - 1].element
      .querySelector("a[href], button")
      ?.focus({ preventScroll: true });
  });
  new MutationObserver(() => {
    const open = isOpen();
    if (open !== wasOpen) {
      wasOpen = open;
      redirectOpeningFocus = open && Boolean(logoLink);
      // Reset on opening: clearing the title link while closing can remove
      // its href before the browser follows the link that was just clicked.
      if (!open) return;
      syncChrome();
      trail = [root];
      panels.forEach(({ element }) => {
        element.scrollTop = 0;
      });
      render();
    }
  }).observe(container, { attributes: true, attributeFilter: ["class"] });

  container.addEventListener(
    "keydown",
    (event) => {
      if (!isOpen()) return;
      if (event.key === "Escape" && trail.length > 1) {
        event.preventDefault();
        event.stopPropagation();
        goBack(false);
        return;
      }
      if (event.key !== "Tab") return;

      // Core caches its focus boundaries when opening. Navigation levels change
      // afterwards, so calculate the visible targets for every Tab instead.
      const focusable = [
        ...container.querySelectorAll("a[href], button:not([disabled])"),
      ].filter(
        (element) =>
          !element.closest("[inert]") &&
          element.getClientRects().length > 0 &&
          getComputedStyle(element).visibility !== "hidden"
      );
      if (!focusable.length) return;
      const index = focusable.indexOf(document.activeElement);
      const afterTitle = focusable.findIndex(
        (element) =>
          title.compareDocumentPosition(element) &
          Node.DOCUMENT_POSITION_FOLLOWING
      );
      const next =
        index < 0
          ? document.activeElement === title && afterTitle >= 0
            ? (afterTitle - (event.shiftKey ? 1 : 0) + focusable.length) %
              focusable.length
            : event.shiftKey
            ? focusable.length - 1
            : 0
          : (index + (event.shiftKey ? -1 : 1) + focusable.length) %
            focusable.length;
      event.preventDefault();
      event.stopPropagation();
      focusable[next].focus();
    },
    true
  );

  menu.addEventListener("click", (event) => {
    if (event.target instanceof Element && event.target.closest("a[href]")) {
      closeButton.click();
    }
  });
  window.addEventListener(
    "resize",
    () => {
      // Respect the actual editor/plugin breakpoint, including tablet layouts.
      if (isOpen()) {
        if (openButton.getClientRects().length === 0) closeButton.click();
        else syncChrome();
      }
    },
    { passive: true }
  );
};

export const initMobileNavigation = () => {
  document
    .querySelectorAll(".wp-block-navigation__responsive-container")
    .forEach(createMenu);
};
